import { useQuery } from "@tanstack/react-query";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import {
  classifyApplication,
  type PetRelationshipKind,
  type ReservationApplication,
} from "./petProfileModel";

type PetNotesRow = Pick<
  Database["public"]["Tables"]["pets"]["Row"],
  "daycare_hotel_notes"
>;

type PetProfileSource = {
  applications: Array<{
    id: string;
    petId: string;
    clinicId: string;
    itemId: string | null;
    nextApplicationItemId: string | null;
    applicationDate: string;
    doseNumber: number | null;
    batchNumber: string | null;
    expiryDate: string | null;
    adverseReaction: string | null;
    notes: string | null;
    item: { name: string | null } | null;
    nextApplicationItem: { name: string | null } | null;
    nextDoseDate: string | null;
    hasNextDose: boolean;
    appointmentId: string | null;
    medicalRecordId: string | null;
  }>;
  plans: Array<{
    id: string;
    petId: string;
    clinicId: string;
    itemId: string;
    dueDate: string;
    notes: string | null;
    appointmentId: string | null;
    sourceType: "manual" | "derived_from_application";
    sourcePetApplicationId: string | null;
    item: { name: string | null } | null;
  }>;
  conditions: Array<{ id: string; name: string; type: string }>;
  alerts: PetCustomAlert[];
  relationships: PetRelationshipSummary[];
  storedItems: StoredPetItem[];
  notes: {
    daycareHotelNotes: string | null;
  };
};

export type PetCustomAlert = {
  id: string;
  label: string;
  value: string;
  icon: string | null;
  color: string | null;
};

export type PetRelationshipSummary = {
  id: string;
  petId: string;
  relatedPetId: string;
  petName: string;
  petPhotoPath: string | null;
  relationship: PetRelationshipKind;
  description: string | null;
};

type PetRelationshipSelectRow = {
  id: string;
  pet_id: string;
  related_pet_id: string;
  relationship: string;
  description: string | null;
};

type PetRelationshipsReadTable = {
  select: (columns: string) => {
    or: (filter: string) => Promise<{ data: PetRelationshipSelectRow[] | null; error: Error | null }>;
  };
};

const petRelationshipsReadTable = () =>
  (supabase as unknown as { from: (table: "pet_relationships") => PetRelationshipsReadTable }).from("pet_relationships");

export type StoredPetItem = {
  id: string;
  itemName: string;
  storageLocation: string | null;
};

export type PetCondition = { id: string; name: string };

export type PetReservationSummary = {
  applications: ReservationApplication[];
  allergies: PetCondition[];
  temperaments: PetCondition[];
  alerts: PetCustomAlert[];
  relationships: PetRelationshipSummary[];
  daycareHotelNotes: string | null;
  storedItems: StoredPetItem[];
};

export const petReservationSummaryKey = (petId: string | null) => ["pet-reservation-summary", petId];

async function fetchPetNotes(petId: string): Promise<PetNotesRow> {
  const { data, error } = await supabase
    .from("pets")
    .select("daycare_hotel_notes")
    .eq("id", petId)
    .maybeSingle();
  if (error) throw error;

  return data ?? { daycare_hotel_notes: null };
}

export function mapPetReservationSummary(
  source: PetProfileSource,
  now = new Date(),
): PetReservationSummary {
  const applications = source.applications.flatMap((application) => {
    const appliedName = application.item?.name;
    if (!appliedName || !application.itemId || !application.hasNextDose || !application.nextDoseDate) {
      return [];
    }
    const nextDoseDate = application.nextDoseDate;
    return [{
      id: application.id,
      name: application.nextApplicationItem?.name ?? appliedName,
      origin: "application" as const,
      petId: application.petId,
      clinicId: application.clinicId,
      itemId: application.itemId,
      nextApplicationItemId: application.nextApplicationItemId,
      applicationDate: application.applicationDate,
      doseNumber: application.doseNumber,
      batchNumber: application.batchNumber,
      expiryDate: application.expiryDate,
      adverseReaction: application.adverseReaction,
      notes: application.notes,
      nextDoseDate,
      hasNextDose: application.hasNextDose,
      appointmentId: application.appointmentId,
      medicalRecordId: application.medicalRecordId,
      item: application.item,
      nextApplicationItem: application.nextApplicationItem,
      status: classifyApplication({
        applicationDate: application.applicationDate,
        nextDoseDate,
      }, now),
    }];
  });
  const visibleApplicationIds = new Set(applications.map((application) => application.id));

  return {
    applications: [
      ...applications,
      ...source.plans.flatMap((plan) => {
        if (
          !plan.item?.name
          || (plan.sourceType === "derived_from_application"
            && plan.sourcePetApplicationId
            && visibleApplicationIds.has(plan.sourcePetApplicationId))
        ) {
          return [];
        }
        return [{
          id: plan.id,
          name: plan.item.name,
          origin: "plan" as const,
          petId: plan.petId,
          clinicId: plan.clinicId,
          itemId: plan.itemId,
          applicationDate: plan.dueDate,
          nextDoseDate: plan.dueDate,
          notes: plan.notes,
          appointmentId: plan.appointmentId,
          item: plan.item,
          sourceType: plan.sourceType,
          sourcePetApplicationId: plan.sourcePetApplicationId,
          status: classifyApplication({
            applicationDate: plan.dueDate,
            nextDoseDate: plan.dueDate,
          }, now),
        }];
      }),
    ],
    allergies: source.conditions
      .filter((condition) => condition.type === "allergy")
      .map((condition) => ({ id: condition.id, name: condition.name })),
    temperaments: source.conditions
      .filter((condition) => condition.type === "temperament")
      .map((condition) => ({ id: condition.id, name: condition.name })),
    alerts: source.alerts,
    relationships: source.relationships,
    daycareHotelNotes: source.notes.daycareHotelNotes,
    storedItems: source.storedItems,
  };
}

export function usePetReservationSummary(petId: string | null) {
  return useQuery({
    queryKey: petReservationSummaryKey(petId),
    enabled: !!petId,
    queryFn: async (): Promise<PetReservationSummary | null> => {
      if (!petId) return null;

      const [notesResult, applicationsResult, plansResult, conditionsResult, alertsResult, relationshipsResult, storedItemsResult] = await Promise.all([
        fetchPetNotes(petId),
        supabase
          .from("pet_applications")
          .select("id, pet_id, clinic_id, item_id, next_application_item_id, application_date, dose_number, batch_number, expiry_date, adverse_reaction, notes, next_dose_date, has_next_dose, appointment_id, medical_record_id, item:items!item_id(name), next_application_item:items!next_application_item_id(name)")
          .eq("pet_id", petId)
          .is("deleted_at", null)
          .eq("has_next_dose", true)
          .not("next_dose_date", "is", null)
          .order("application_date", { ascending: false }),
        supabase
          .from("pet_application_plans")
          .select("id, pet_id, clinic_id, item_id, due_date, notes, appointment_id, source_type, source_pet_application_id, item:items!item_id(name)")
          .eq("pet_id", petId)
          .in("status", ["pending", "scheduled"])
          .is("deleted_at", null)
          .order("due_date", { ascending: true }),
        supabase
          .from("pet_conditions")
          .select("conditions!inner(id, name, type)")
          .eq("pet_id", petId)
          .in("conditions.type", ["allergy", "temperament"]),
        supabase
          .from("pet_custom_alerts")
          .select("id, label, value, icon, color")
          .eq("pet_id", petId)
          .eq("is_active", true)
          .order("created_at"),
        petRelationshipsReadTable()
          .select("id, pet_id, related_pet_id, relationship, description")
          .or(`pet_id.eq.${petId},related_pet_id.eq.${petId}`),
        supabase
          .from("pet_stored_items")
          .select("id, item_name, storage_location")
          .eq("pet_id", petId)
          .eq("is_kept", true)
          .order("created_at"),
      ]);

      if (applicationsResult.error) throw applicationsResult.error;
      if (plansResult.error) throw plansResult.error;
      if (conditionsResult.error) throw conditionsResult.error;
      if (alertsResult.error) throw alertsResult.error;
      if (relationshipsResult.error) throw relationshipsResult.error;
      if (storedItemsResult.error) throw storedItemsResult.error;

      const relationshipRows = relationshipsResult.data ?? [];
      const counterpartIds = relationshipRows.map((row) => row.pet_id === petId ? row.related_pet_id : row.pet_id);
      const [counterpartResult, counterpartPhotosResult] = await Promise.all([
        counterpartIds.length
          ? supabase.from("pets").select("id, name").in("id", counterpartIds)
          : Promise.resolve({ data: [], error: null }),
        counterpartIds.length
          ? supabase.from("pet_photos").select("pet_id, file_path").in("pet_id", counterpartIds).is("deleted_at", null)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (counterpartResult.error) throw counterpartResult.error;
      if (counterpartPhotosResult.error) throw counterpartPhotosResult.error;

      const counterpartNames = new Map((counterpartResult.data ?? []).map((pet) => [pet.id, pet.name]));
      const counterpartPhotoPaths = new Map(
        (counterpartPhotosResult.data ?? []).map((photo) => [photo.pet_id, photo.file_path]),
      );

      const conditions = (conditionsResult.data ?? []).flatMap((row) => {
        const condition = row.conditions as { id: string; name: string; type: string } | null;
        return condition?.name ? [{ id: condition.id, name: condition.name, type: condition.type }] : [];
      });

      return mapPetReservationSummary({
        applications: (applicationsResult.data ?? []).map((application) => ({
          id: application.id,
          petId: application.pet_id,
          clinicId: application.clinic_id,
          itemId: application.item_id,
          nextApplicationItemId: application.next_application_item_id,
          applicationDate: application.application_date,
          doseNumber: application.dose_number,
          batchNumber: application.batch_number,
          expiryDate: application.expiry_date,
          adverseReaction: application.adverse_reaction,
          notes: application.notes,
          item: application.item as { name: string | null } | null,
          nextApplicationItem: application.next_application_item as { name: string | null } | null,
          nextDoseDate: application.next_dose_date,
          hasNextDose: application.has_next_dose,
          appointmentId: application.appointment_id,
          medicalRecordId: application.medical_record_id,
        })),
        plans: (plansResult.data ?? []).map((plan) => ({
          id: plan.id,
          petId: plan.pet_id,
          clinicId: plan.clinic_id,
          itemId: plan.item_id,
          dueDate: plan.due_date,
          notes: plan.notes,
          appointmentId: plan.appointment_id,
          sourceType: plan.source_type as "manual" | "derived_from_application",
          sourcePetApplicationId: plan.source_pet_application_id,
          item: plan.item as { name: string | null } | null,
        })),
        conditions,
        alerts: alertsResult.data ?? [],
        relationships: relationshipRows.flatMap((row) => {
          const counterpartId = row.pet_id === petId ? row.related_pet_id : row.pet_id;
          const petName = counterpartNames.get(counterpartId);
          return petName ? [{
            id: row.id,
            petId: row.pet_id,
            relatedPetId: row.related_pet_id,
            petName,
            petPhotoPath: counterpartPhotoPaths.get(counterpartId) ?? null,
            relationship: row.relationship as PetRelationshipKind,
            description: row.description,
          }] : [];
        }),
        storedItems: (storedItemsResult.data ?? []).map((item) => ({
          id: item.id,
          itemName: item.item_name,
          storageLocation: item.storage_location,
        })),
        notes: {
          daycareHotelNotes: notesResult.daycare_hotel_notes,
        },
      });
    },
  });
}
